import React, { useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { LanguageConfigSource, MeetingLanguage, RoomSessionLanguageConfig } from '../../models/Room'
import { User } from '../../models/User'
import { useAppSelector } from '../../store/hooks'
import { selectRoomSession } from '../../store/meetingSummary/roomSession'
import { getRoomTrackingDetails } from '../../apps/professional-app/fa/tracking'
import { SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { Button } from '../../components/Button/Button'
import { Modal } from '../../components/Modal/Modal'
import { PersonIcon } from '../../components/Common/PersonIcon'
import { getContactLanguageLabel, getLanguageLabel, getLanguageSource, LANGUAGE_OPTIONS } from '../../common/languageUtils'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { useMultiLanguageEnabled } from '../../common/useMultiLanguageEnabled'

export type LanguageChangeReason = 'client_added' | 'client_removed' | 'manual'

type ChangeMeetingLanguageModalProps = {
  onHide: () => void
  languageConfig?: RoomSessionLanguageConfig | null
  calculatedLanguageConfig?: RoomSessionLanguageConfig | null
  oldLanguageConfig?: RoomSessionLanguageConfig | null
  participants?: User[]
  onUpdateLanguage: (language: RoomSessionLanguageConfig) => void
  isUpdatingSessionConfig: boolean
  title: string
  changeReason?: LanguageChangeReason
  onAcknowledge?: (language: MeetingLanguage, changeReason: LanguageChangeReason) => void
  confirmButtonText: string
}

export const ChangeMeetingLanguageModal: React.FC<ChangeMeetingLanguageModalProps> = ({
  onHide,
  languageConfig,
  calculatedLanguageConfig,
  oldLanguageConfig,
  participants,
  onUpdateLanguage,
  isUpdatingSessionConfig,
  title,
  changeReason = 'manual',
  onAcknowledge,
  confirmButtonText,
}) => {
  const isInitiallyCalculated = languageConfig
    ? languageConfig.source !== LanguageConfigSource.MANUAL && languageConfig.source !== LanguageConfigSource.DEFAULT
    : false
  const [selectedLanguage, setSelectedLanguage] = useState<MeetingLanguage>(languageConfig?.language ?? MeetingLanguage.ENGLISH)
  const [isCalculatedSelected, setIsCalculatedSelected] = useState<boolean>(isInitiallyCalculated)
  const roomSession = useAppSelector(selectRoomSession)
  const { multiLanguageEnabled } = useMultiLanguageEnabled()
  const isClientRole = (user: User) => user.roles.some((role) => role.name === 'Client')

  const calculatedLanguageLabel = 'Determine based on clients language'

  const dropdownOptions = useMemo(() => {
    if (!calculatedLanguageConfig?.language) {
      return LANGUAGE_OPTIONS
    }
    const calculatedOption = {
      id: `calculated-${calculatedLanguageConfig.language}`,
      label: calculatedLanguageLabel,
    }
    return [calculatedOption, ...LANGUAGE_OPTIONS]
  }, [calculatedLanguageConfig?.language, calculatedLanguageLabel])

  const selectedOption = useMemo(() => {
    if (isCalculatedSelected) {
      return dropdownOptions.find((opt) => opt.id.startsWith('calculated-')) || null
    }
    return LANGUAGE_OPTIONS.find((opt) => opt.id === selectedLanguage) || LANGUAGE_OPTIONS[0]
  }, [dropdownOptions, selectedLanguage, isCalculatedSelected])

  const clientParticipants = useMemo(
    () => (participants ? participants.filter(isClientRole) : (roomSession?.users ?? []).filter(isClientRole)),
    [participants, roomSession?.users],
  )
  const clientsWithoutLanguage = clientParticipants.filter((user) => !user.settings?.language).length > 0
  const { tracked } = useTracking()

  const hasSelectionChanged = isCalculatedSelected !== isInitiallyCalculated || selectedLanguage !== languageConfig?.language

  const handleUpdate = () => {
    const finalLanguage = isCalculatedSelected ? calculatedLanguageConfig?.language : selectedLanguage

    if (!hasSelectionChanged) {
      if (finalLanguage) {
        onAcknowledge?.(finalLanguage, changeReason)
      }
      onHide()
      return
    }

    if (isCalculatedSelected && calculatedLanguageConfig) {
      onUpdateLanguage(calculatedLanguageConfig)
    } else {
      const manualConfig: RoomSessionLanguageConfig = {
        language: selectedLanguage,
        source: LanguageConfigSource.MANUAL,
        updatedAt: new Date().toISOString(),
      }
      onUpdateLanguage(manualConfig)
    }
    if (finalLanguage) {
      onAcknowledge?.(finalLanguage, changeReason)
    }
    onHide()
  }

  const handleLanguageUpdated = tracked(handleUpdate, {
    eventName: TrackEvent.MEETING_LANGUAGE_CONFIRMED,
    eventDetails: {
      ...getRoomTrackingDetails(roomSession),
      oldLanguage: languageConfig?.language,
      newLanguage: isCalculatedSelected ? calculatedLanguageConfig?.language : selectedLanguage,
      source: isCalculatedSelected ? 'calculated' : 'manual',
    },
  })

  const handleLanguageChange = (option: SelectOption) => {
    const isCalculatedOption = option.id.startsWith('calculated-')
    const languageId = isCalculatedOption ? option.id.replace('calculated-', '') : option.id
    setSelectedLanguage(languageId as MeetingLanguage)
    setIsCalculatedSelected(isCalculatedOption)
  }

  if (!languageConfig || !multiLanguageEnabled) {
    return null
  }

  const modalHeader = <h4 className='mb-3'>{title}</h4>

  const body = (
    <Container>
      <Row>
        <Col>
          {oldLanguageConfig ? (
            <div className='paragraph-medium zocks-mb-1'>
              Meeting language changed from <strong>{getLanguageLabel(oldLanguageConfig.language)}</strong>
              {getLanguageSource(oldLanguageConfig)} to <strong>{getLanguageLabel(languageConfig?.language)}</strong>
              {languageConfig ? getLanguageSource(languageConfig) : null}. You can set the meeting language manually if needed.
            </div>
          ) : null}
          <div className='zocks-mb-1'>
            <div className='d-flex flex-column gap-1'>
              <div>
                <p className='paragraph-small mb-0'>Meeting language</p>
              </div>
              <SelectDropdown
                options={dropdownOptions}
                initialSelectedOption={selectedOption}
                placeholder='Select language...'
                icon='bi-globe2'
                onOptionSelected={handleLanguageChange}
              />
              {isCalculatedSelected ? (
                <div className='paragraph-small pt-2'>
                  Meeting language is <strong>{getLanguageLabel(calculatedLanguageConfig?.language)}</strong>
                  {calculatedLanguageConfig ? `${getLanguageSource(calculatedLanguageConfig)}` : null}
                </div>
              ) : null}
            </div>
          </div>
          {isCalculatedSelected ? (
            <div className='paragraph-small mb-4'>
              Determined language <strong>could change later</strong> by adding or removing clients.
            </div>
          ) : (
            <div className='paragraph-small mb-4'>
              <strong>Manually set language won't be overridden</strong> by adding or removing clients.
            </div>
          )}
          {isCalculatedSelected && calculatedLanguageConfig?.language == MeetingLanguage.AUTO_DETECT ? (
            <div className='d-flex align-items-center gap-2 py-3 px-3 mb-0 zocks-border-color-warning-dark zocks-border-0.25 mt-2'>
              <i className='zocks-text-warning-light bi bi-exclamation-triangle-fill flex-shrink-0' />
              <span className='paragraph-small'>
                AI Assistant detects the spoken language during the meeting. If detection is wrong, <strong> your transcription will be inaccurate </strong>.
                Since Zocks produces <strong>no recordings</strong>, after the meeting, <strong>transcriptions cannot be recreated.</strong>
              </span>
            </div>
          ) : null}
          {clientParticipants.length > 0 ? (
            <div>
              <div className='d-flex flex-column zocks-mb-2'>
                <div className='px-2'>
                  <div className='d-flex justify-content-between align-items-center py-3 border-bottom'>
                    <p className='lead-extra-small mb-0 w-75'>Client</p>
                    <p className='lead-extra-small mb-0 w-25'>Language</p>
                  </div>
                  {clientParticipants.map((user, index) => (
                    <div
                      key={user.id}
                      className={`d-flex justify-content-between align-items-center py-3 ${index < clientParticipants.length - 1 ? 'border-bottom' : ''}`}
                    >
                      <div className='d-flex gap-2 align-items-center w-50'>
                        <PersonIcon />
                        <span className='paragraph-medium bold'>{user.name || user.email}</span>
                      </div>
                      <div className='w-25'>
                        <span className={`paragraph-medium ${!user.settings?.language ? 'zocks-text-gray-600' : ''}`}>
                          {user.settings?.language ? getContactLanguageLabel(user.settings?.language) : 'Not set'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {clientsWithoutLanguage ? (
                <div className='bg-light border rounded p-2 d-flex gap-3 align-items-center mb-3'>
                  <i className='bi bi-info-circle' />
                  <p className='paragraph-small mb-0'>Clients without a language will have the meeting language added to their profile after the meeting.</p>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className='d-flex gap-3 justify-content-end py-3'>
            <Button variant='primary' onClick={handleLanguageUpdated} loading={isUpdatingSessionConfig}>
              {confirmButtonText}
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  )
  return <Modal show header={modalHeader} body={body} backdrop='static' keyboard={false} />
}
